import { useMemo } from "react";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";

/**
 * Derives the set of team guest aliases for a category from the roster that
 * already lives in the Redux store — no new API, no new state field.
 *
 * `teamAndAllGuestOwnerOptions` is the merged, correctly-deduped roster set by
 * the ui-content slices: members are `{ label: alias, value: alias }`, guests
 * are `{ label: alias + " (Guest)", value: alias }`, and the not-yet-loaded
 * sentinel is `{ label: "No owner", value: "No owner" }`. So `label !== value`
 * IS the guest predicate, and the member-wins dedupe is already applied
 * upstream by `mergeMembersAndGuests`.
 *
 * The three slices hold the SAME roster (dispatched together by
 * `useTeamMembersLoader`), so `category` selects the conventionally-correct
 * slice rather than different data — it cannot make a suffix appear on one
 * surface and not another.
 */
export const useTeamGuestAliases = (category?: string): Set<string> => {
  const { teamAndAllGuestOwnerOptions } = useGaiaObjectUiContent(category);
  return useMemo(
    () =>
      new Set(
        (teamAndAllGuestOwnerOptions ?? [])
          .filter((o) => typeof o.value === "string" && o.label !== o.value)
          .map((o) => o.value as string),
      ),
    [teamAndAllGuestOwnerOptions],
  );
};
